import type { AttendanceRule } from "./types";

const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_FREQUENCY_MINUTES = 5;

export function calculateAttendanceCount(rule: AttendanceRule, now = new Date()): number {
  const startMinute = parseClockMinutes(rule.startTime);
  const endMinute = parseClockMinutes(rule.endTime);
  const duration = Math.max(1, normalizeDuration(startMinute, endMinute));
  const frequencyMinutes = normalizeFrequency(rule.frequencyMinutes);
  const resetAt = parseResetAt(rule.resetAt);
  const cycleStart = resetAt && isSameLocalDay(resetAt, now) ? resetAt : dateAtClockMinute(now, startMinute);
  const cycleEnd = addMinutes(cycleStart, duration);

  if (now.getTime() < cycleStart.getTime()) {
    return rule.startCount;
  }

  if (now.getTime() >= cycleEnd.getTime()) {
    return rule.targetCount;
  }

  const elapsedMinutes = Math.max(0, Math.floor((now.getTime() - cycleStart.getTime()) / 60_000));
  const elapsedBuckets = Math.floor(elapsedMinutes / frequencyMinutes);
  const totalBuckets = Math.max(1, Math.ceil(duration / frequencyMinutes));
  const progress = Math.min(1, elapsedBuckets / totalBuckets);
  const base = rule.startCount + (rule.targetCount - rule.startCount) * progress;
  const jitter = elapsedBuckets === 0 ? 0 : deterministicJitter(rule.seed, now, rule.jitter, frequencyMinutes);

  return clamp(Math.round(base + jitter), rule.startCount, rule.targetCount);
}

function parseClockMinutes(value: string): number {
  const [hourText, minuteText] = value.split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);

  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    throw new Error(`Invalid clock time: ${value}`);
  }

  return hour * 60 + minute;
}

function normalizeDuration(startMinute: number, endMinute: number): number {
  if (endMinute >= startMinute) {
    return endMinute - startMinute;
  }

  return MINUTES_PER_DAY - startMinute + endMinute;
}

function normalizeFrequency(value: number | undefined): number {
  if (value === undefined || !Number.isFinite(value)) {
    return DEFAULT_FREQUENCY_MINUTES;
  }

  return clamp(Math.round(value), 1, 20);
}

function parseResetAt(value: string | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function isSameLocalDay(left: Date, right: Date): boolean {
  return left.getFullYear() === right.getFullYear() && left.getMonth() === right.getMonth() && left.getDate() === right.getDate();
}

function dateAtClockMinute(date: Date, minuteOfDay: number): Date {
  const next = new Date(date);
  next.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60, 0, 0);
  return next;
}

function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

function deterministicJitter(seed: string, now: Date, spread: number, frequencyMinutes: number): number {
  if (spread <= 0) {
    return 0;
  }

  const bucket = Math.floor((now.getHours() * 60 + now.getMinutes()) / frequencyMinutes);
  const key = `${seed}:${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}:${bucket}`;
  let hash = 2166136261;

  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  const normalized = (hash >>> 0) / 0xffffffff;
  return Math.round((normalized * 2 - 1) * spread);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
